import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  FormTextarea,
} from "@/components/ui";
import { getErrorMessage, isApiError } from "@/api/client";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { useSendSms } from "@/api/hooks/useInteractions";

interface ComposeSmsDialogProps {
  open: boolean;
  onClose: () => void;
  contactId: string;
}

const SEGMENT_LENGTH = 160;

/** "0 characters" / "42/160 · 1 segment" / "165 characters · 2 segments". */
function segmentSummary(length: number): string {
  if (length === 0) return "0 characters";
  const segments = Math.ceil(length / SEGMENT_LENGTH);
  const segLabel = segments === 1 ? "segment" : "segments";
  return `${length} characters · ${segments} ${segLabel}`;
}

/**
 * Compose + send an outbound text to a contact (Phase 12). Single "Message"
 * textarea with a live character/segment counter (160 chars per SMS
 * segment). The send is additive to the same timeline as logged
 * interactions and the Phase-11 email compose flow — same dialog shape,
 * same invalidate-on-success pattern.
 *
 * The Text button stays enabled even when the contact hasn't consented to
 * SMS (sms_consent === false); the API 403s in that case, and we surface
 * that error with an explicit hint to get consent first rather than
 * silently blocking the send client-side.
 */
export function ComposeSmsDialog({ open, onClose, contactId }: ComposeSmsDialogProps) {
  const [message, setMessage] = useState("");

  const sendSms = useSendSms();

  useEffect(() => {
    if (open) {
      setMessage("");
    }
  }, [open]);

  const canSubmit = !sendSms.isPending && message.trim() !== "";

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      await sendSms.mutateAsync({ contactId, body: message.trim() });
      toastSuccess("Text sent");
      onClose();
    } catch (err) {
      if (isApiError(err) && err.response?.status === 403) {
        toastError(
          "No SMS consent on file",
          `${getErrorMessage(err)} — get their consent before texting.`,
        );
      } else {
        toastError("Couldn't send text", getErrorMessage(err));
      }
    }
  }

  return (
    <Dialog open={open} onClose={onClose} ariaLabel="Send text">
      <DialogContent size="md">
        <DialogHeader onClose={onClose}>Send text</DialogHeader>
        <DialogBody className="space-y-2">
          <FormTextarea
            label="Message"
            required
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <p className="text-xs text-text-muted" data-testid="sms-segment-counter">
            {segmentSummary(message.length)}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={sendSms.isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={!canSubmit}>
            {sendSms.isPending ? "Sending..." : "Send text"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
